import { applyTenantDocumentBranding } from '@/lib/tenant-document-branding';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { brandingApi } from '@/api/supports';
import { TenantBranding } from '@/types';
import { useTenantContext } from './useTenantContext';
import { hexToHslParts, readableForeground } from '@/lib/color';

// Theme vars we override from tenant branding. Listed so we can reset them all
// (e.g. when a SuperAdmin exits impersonation) and fall back to the class theme.
const BRAND_OVERRIDES = [
  '--primary',
  '--primary-foreground',
  '--ring',
  '--sidebar-primary',
  '--sidebar-primary-foreground',
  '--sidebar-ring',
  '--brand-primary',
  '--brand-secondary',
  '--brand-accent',
  '--font-sans',
  '--font-heading',
];

const TENANT_FONT_STYLE_ID = 'tenant-brand-font-face';

export function useBrandingTheme() {
  const { i18n } = useTranslation();
  const { isSuperAdmin, impersonatedTenantId } = useTenantContext();
  const isImpersonating = isSuperAdmin && impersonatedTenantId != null;
  // Fetch branding for a real tenant admin, OR a SuperAdmin impersonating a
  // tenant. A SuperAdmin on the platform menu uses the Heglo brand (no fetch).
  const shouldApply = !isSuperAdmin || isImpersonating;

  const { data: branding } = useQuery<TenantBranding>({
    // Key by impersonated tenant so switching tenants refetches its branding.
    queryKey: ['branding', impersonatedTenantId ?? null],
    queryFn: brandingApi.get,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    enabled: shouldApply,
  });

  const websiteBranding = shouldApply ? branding?.website : undefined;
  const isArabic = i18n.language.startsWith('ar');
  const selectedFontFamily = isArabic ? websiteBranding?.arabicFontFamily : websiteBranding?.fontFamily;
  const fallbackFont = isArabic ? '"IBM Plex Sans Arabic", "Cairo", "Inter", system-ui, sans-serif' : '"Inter", "IBM Plex Sans Arabic", system-ui, sans-serif';
  const selectedFontUrl = isArabic ? websiteBranding?.arabicFontFileUrl : websiteBranding?.fontFileUrl;

  useEffect(() => {
    const root = document.documentElement;

    // No tenant branding in effect (platform SuperAdmin, or exited
    // impersonation): strip any overrides so the class theme takes over.
    if (!websiteBranding) {
      BRAND_OVERRIDES.forEach((p) => root.style.removeProperty(p));
      document.getElementById(TENANT_FONT_STYLE_ID)?.remove();
      return;
    }

    const primaryHsl = hexToHslParts(websiteBranding.primaryColor);
    if (primaryHsl) {
      // Buttons / focus rings / links and the active sidebar item adopt the
      // tenant's primary; foreground flips to stay legible on that color.
      const fg = readableForeground(websiteBranding.primaryColor);
      root.style.setProperty('--primary', primaryHsl);
      root.style.setProperty('--primary-foreground', fg);
      root.style.setProperty('--ring', primaryHsl);
      root.style.setProperty('--sidebar-primary', primaryHsl);
      root.style.setProperty('--sidebar-primary-foreground', fg);
      root.style.setProperty('--sidebar-ring', primaryHsl);
    }

    // Keep the raw brand vars too (consumed by any brand-specific styling).
    if (websiteBranding.primaryColor) root.style.setProperty('--brand-primary', websiteBranding.primaryColor);
    if (websiteBranding.secondaryColor) root.style.setProperty('--brand-secondary', websiteBranding.secondaryColor);
    if (websiteBranding.accentColor) root.style.setProperty('--brand-accent', websiteBranding.accentColor);

    if (selectedFontUrl) {
      const style = document.createElement('style');
      style.id = TENANT_FONT_STYLE_ID;
      style.textContent = `@font-face{font-family:'TenantUploadedFont';src:url(${JSON.stringify(selectedFontUrl)});font-style:normal;font-weight:100 900;font-display:swap;}`;
      document.getElementById(TENANT_FONT_STYLE_ID)?.remove();
      document.head.appendChild(style);
      root.style.setProperty('--font-sans', `'TenantUploadedFont', ${fallbackFont}`);
      root.style.setProperty('--font-heading', `'TenantUploadedFont', ${fallbackFont}`);
    } else if (selectedFontFamily) {
      root.style.setProperty('--font-sans', `${selectedFontFamily}, ${fallbackFont}`);
      root.style.setProperty('--font-heading', `${selectedFontFamily}, ${fallbackFont}`);
    }

    // Reset on unmount / before re-applying for a different tenant.
    return () => {
      BRAND_OVERRIDES.forEach((p) => root.style.removeProperty(p));
      document.getElementById(TENANT_FONT_STYLE_ID)?.remove();
    };
  }, [fallbackFont, selectedFontFamily, selectedFontUrl, websiteBranding]);

  useEffect(()=>{if(websiteBranding)applyTenantDocumentBranding(websiteBranding,undefined,i18n.language);},[websiteBranding,i18n.language]);

  return {
    branding: websiteBranding,
    logoUrl: websiteBranding?.logoUrl,
    appName: websiteBranding?.appName,
    appNameArabic: websiteBranding?.appNameArabic,
  };
}
